import {
  Collection,
  Loadable,
  Model,
  ModelAttributes,
  ModelDescription,
  collection,
  model,
  modelConfig,
  reference,
} from "@mixitone/oom";
import { isNil, isPresent } from "@mixitone/util";
import Club from "./Club";
import FundingAppeal from "./FundingAppeal";
import TournamentCompetition from "./TournamentCompetition";
import TournamentPaymentMethod from "./TournamentPaymentMethod";
import { setAccountId } from "./concerns/SetAccountId";
import { Database } from "./database.types";

type Status = Database["public"]["Enums"]["tournament_session_status"];

export const TournamentSessionStatus: Record<Status, string> = {
  active: "Active",
  completed: "Completed",
  entry_open: "Entries Open",
  registration: "Registration",
  setup: "Setup",
};

export type PaymentBy = "player" | "entry";
export const TournamentPaymentBy: Record<PaymentBy, string> = {
  player: "By player",
  entry: "By entry",
};

const config = modelConfig(
  {
    account_id: {
      type: "string",
    },
    banner_image_id: {
      type: "string",
    },
    club_id: {
      type: "string",
    },
    completed_at: {
      type: "datetime",
    },
    confirmation_text: {
      type: "string",
    },
    courts: {
      type: "number",
    },
    created_at: {
      type: "datetime",
    },
    created_by: {
      type: "string",
    },
    description: {
      type: "string",
    },
    ends_at: {
      type: "datetime",
    },
    is_deleted: {
      type: "boolean",
    },
    is_public: {
      type: "boolean",
    },
    is_test_mode: {
      type: "boolean",
    },
    name: {
      type: "string",
    },
    payment_by_player_or_entry: {
      type: "string",
    },
    portal_slug: {
      type: "string",
    },
    registration_closes_at: {
      type: "datetime",
    },
    short_code: {
      type: "string",
    },
    stand_down_minutes: {
      type: "number",
    },
    starts_at: {
      type: "datetime",
    },
    status: {
      type: "enum",
      enumType: "" as Database["public"]["Enums"]["tournament_session_status"],
    },
    thank_you_sent_at: {
      type: "datetime",
    },
    thank_you_text: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
  },
  {
    competitions: collection<any>(() => TournamentCompetition as any, "tournament_session_id"),
    funding_appeals: collection<any>(() => FundingAppeal as any, "tournament_session_id"),
    paymentMethods: collection<any>(() => TournamentPaymentMethod as any, "tournament_session_id"),
  },
  {
    club: reference(() => Club as any, "club_id"),
  },
);

interface TournamentSession extends ModelAttributes<typeof config> {
  competitions: Collection<TournamentCompetition>;
  funding_appeals: Collection<FundingAppeal>;
  paymentMethods: Collection<TournamentPaymentMethod>;
  club: Loadable<Club>;
}

@setAccountId
@model("tournament_sessions", config)
class TournamentSession extends Model<typeof config> {
  static readonly modelDescription: ModelDescription = {
    description:
      "A tournament session represents a complete tournament event that contains multiple competitions. " +
      "It has a lifecycle from setup through entry period, registration, active play, and completion. " +
      "Sessions are associated with a specific club and can have multiple competitions running in parallel.",
    availableFields: Object.keys(config.fields),
    relationships: {
      competitions: "tournament_competitions where tournament_session_id equals this id",
      club: "clubs where id equals this club_id",
      paymentMethods: "tournament_payment_methods where tournament_session_id equals this id",
    },
  };

  override save(): Promise<void> {
    if (isNil(this.payment_by_player_or_entry)) {
      this.payment_by_player_or_entry = "player";
    }
    return super.save();
  }

  get isSetup() {
    return this.status === "setup";
  }

  get isEntryOpen() {
    return this.status === "entry_open";
  }

  get isSetupOrEntry() {
    return this.isSetup || this.isEntryOpen;
  }

  get registrationAllowed() {
    return this.isRegistration || this.isActive;
  }

  get isRegistration() {
    return this.status === "registration";
  }

  get isActive() {
    return this.status === "active";
  }

  get isCompleted() {
    return this.status === "completed";
  }

  get hasThankYouBeenSent() {
    return isPresent(this.thank_you_sent_at);
  }

  get canStartEntryPeriod() {
    return this.isSetup;
  }

  get canStartRegistration() {
    return this.isSetup || this.isEntryOpen;
  }

  get canReopenEntryPeriod() {
    return this.isRegistration;
  }

  get canStartTournaments() {
    return this.isRegistration;
  }

  get requiresPayment() {
    return this.paymentMethods.count() > 0;
  }

  registrationLink(portalUrl: string) {
    if (isNil(this.portal_slug)) {
      return null;
    }
    return `${portalUrl}/tournament/${this.portal_slug}`;
  }

  shortRegistrationLink(siteUrl: string) {
    if (isNil(this.short_code)) {
      return null;
    }
    return `${siteUrl.replace(/\/$/, "")}/${this.short_code}`;
  }

  async startEntryPeriod() {
    if (!this.canStartEntryPeriod) throw new Error("Cannot start entry period");

    this.status = "entry_open";
    await this.save();
  }

  async startRegistration() {
    if (!this.canStartRegistration) throw new Error("Cannot start registration");

    // Prefer a DB query over the association: session subscription replace +
    // setupModelRelations can leave competitions loaded-but-empty on Tauri.
    // Use this.competitions.query (lazy model()) to avoid circular import issues
    // with a direct TournamentCompetition.query() call in the Vite bundle.
    const competitions = await this.competitionsForStatusTransition();
    const competitionsToStart = competitions.filter((c) => c.canStartRegistration);

    // Persist competitions first. Saving the session alone leaves portal signup open
    // (portal filters competitions by status=entry_open, not session status).
    await Promise.all(competitionsToStart.map((c) => c.startRegistration()));

    this.status = "registration";
    await this.save();
  }

  async reopenEntryPeriod() {
    if (!this.canReopenEntryPeriod) throw new Error("Cannot reopen entry period");

    const competitions = await this.competitionsForStatusTransition();
    const competitionsToReopen = competitions.filter((c) => c.canReopenEntryPeriod);

    await Promise.all(competitionsToReopen.map((c) => c.reopenEntryPeriod()));

    this.status = "entry_open";
    await this.save();
  }

  /**
   * Fresh query so we do not depend on association wiring that can be emptied by
   * session model replace / setupModelRelations races on Tauri. Reload each row so
   * pendingAttributeChanges cannot leave a stale in-memory status that makes
   * canStartRegistration falsely false.
   */
  private async competitionsForStatusTransition(): Promise<TournamentCompetition[]> {
    const competitions = await this.competitions.query.all();
    await Promise.all(competitions.map((competition) => competition.reload()));
    return competitions;
  }

  async startTournaments() {
    if (!this.canStartTournaments) throw new Error("Cannot start tournaments");

    this.status = "active";
    await this.save();

    if (this.competitions.loaded) {
      console.log("Starting tournaments");
      await Promise.all(this.competitions.map((c) => c.start()));
    } else {
      console.log("Competitions not loaded, cannot start tournaments");
    }
  }

  async complete() {
    if (this.isCompleted) throw new Error("Already completed");

    this.status = "completed";
    this.completed_at = new Date();
    await this.save();
  }

  /**
   * Reset tournament back to registration state (development only)
   */
  async resetToRegistration() {
    if (!this.isActive) throw new Error("Can only reset active tournaments");

    this.status = "registration";
    await this.save();
  }
}

export default TournamentSession;
